<template>
  <div class="slds-grid">
    <template v-if="editable">
      <div class="button-container slds-p-right--small">
        <button
          class="slds-button slds-button_icon slds-button_icon-border-filled delete-button"
          :title="$t('action.delete')"
          @click="$emit('delete', { index })"
          v-if="editable">
          <svg class="slds-button__icon" aria-hidden="true">
            <use xlink:href="../../../../assets/symbols.svg#delete"></use>
          </svg>
        </button>
      </div>

      <!-- Table -->
      <picklist
        sort-label
        class="slds-col slds-size_4-of-24 slds-p-right--small item"
        labels-prefix="campaignDesigner.targetDefinition.table"
        ref="item_table"
        small
        :label="$t('campaignDesigner.targetDefinition.table.label')"
        :options="Object.keys(config)"
        :validation="['required']"
        v-model="table" />

      <!-- Field -->
      <picklist
        sort-label
        class="slds-col slds-size_4-of-24 slds-p-right--small item"
        labels-prefix="campaignDesigner.targetDefinition.field"
        ref="item_field"
        :label="$t('campaignDesigner.targetDefinition.field.label')"
        :disabled="!table"
        :validation="['required']"
        :options="fields"
        v-if="table"
        v-model="field" />

      <!-- Operator -->
      <picklist
        class="slds-col slds-size_4-of-24 slds-p-right--small item"
        labels-prefix="operators"
        no-label
        ref="item_operator"
        small
        :disabled="!field"
        :options="operators"
        :validation="['required']"
        v-if="table && field"
        v-model="operator" />

      <!-- Values -->
      <template v-if="['eq', 'in', 'ne', 'notin'].includes(operator) && !['integer', 'date'].includes(fieldType)">
        <picklist
          sort-label
          class="slds-col slds-size_4-of-24 slds-p-right--small item"
          ref="item_value"
          searchable
          :label="$t('campaignDesigner.targetDefinition.values')"
          :labels-prefix="labelsPrefix"
          :multipicklist="operator === 'in' || operator === 'notin'"
          :options="options"
          :validation="['required']"
          v-model="values" />
      </template>
      <template v-if="fieldType === 'integer'">
        <Input
          :label="$t('campaignDesigner.targetDefinition.values')"
          :validation="['required']"
          type="number"
          v-model="values" />
      </template>
      <template v-if="fieldType === 'date' && ['eq', 'ne', 'gte', 'lte'].includes(operator)">
        <Input
          :label="$t('campaignDesigner.targetDefinition.values')"
          :validation="['required']"
          type="date"
          v-model="values" />
      </template>
    </template>
    <span v-else v-html="itemSummary" class="slds-p-vertical--x-small" />
  </div>
</template>
<script>
import _ from 'lodash'
import moment from 'moment'
import { validate } from '../../../utils/utils'
import Input from '../../../slds/Input'
import Picklist from '../../../slds/Picklist'

const PREFIX_MAP = {
  brand: 'brands',
  cod_market: 'markets',
  doubleOptIn: 'consent',
  gender: 'campaignDesigner.targetDefinition.gender',
  lp_brand: 'brands',
  markedAsHardBounceback: 'consent',
  party_type: 'campaignDesigner.targetDefinition.clientType',
  privacy_email: 'privacy',
  privacy_mail: 'privacy',
  privacy_tel: 'privacy',
  privacy_sms: 'privacy',
  quote_brand: 'brands'
}

export default {
  props: {
    brands: Array,
    config: Object,
    editable: Boolean,
    exclude: Boolean,
    groupOperator: String,
    index: Number,
    scope: String,
    value: Object
  },
  components: {
    Input,
    Picklist
  },
  methods: {
    closeModal () {
      this.showAddGroupModal = false
      this.newGroup = {
        operator: '',
        segmentName: ''
      }
    },
    doUpdate () {
      let values = this.values

      if (this.fieldType === 'date') {
        if (moment(values).isValid()) {
          values = moment(values).format('YYYY-MM-DD')
        } else {
          values = null
        }
      }

      const payload = {
        id: this.id,
        entity: this.config[this.table] ? this.config[this.table].tableName : null,
        field: this.field,
        operator: this.operator,
        segment: this.segment,
        table: this.table,
        type: 'rule',
        values
      }
      this.$emit('input', payload)
    },
    showModal () {
      this.showAddGroupModal = true
    },
    update: _.debounce((self) => self.doUpdate(), 100),
    validate () {
      return validate(this, 'item')
    }
  },
  data () {
    return {
      id: -1,
      field: '',
      operator: '',
      table: '',
      values: ''
    }
  },
  created () {
    const { id, field, operator, table, values } = _.cloneDeep(this.value)
    this.id = id
    this.table = table

    this.$nextTick(() => {
      this.field = field

      this.$nextTick(() => {
        this.operator = operator

        this.$nextTick(() => {
          this.values = values
        })
      })
    })
  },
  computed: {
    fields () {
      if (this.table) {
        return Object.keys(this.config[this.table].fields)
      } else {
        return []
      }
    },
    fieldType () {
      if (this.table && this.field) {
        if (!this.config[this.table].fields[this.field]) {
          return 'text'
        } else {
          return this.config[this.table].fields[this.field].type
        }
      } else {
        return ''
      }
    },
    itemSummary () {
      if (!this.editable && this.table && this.field && this.operator) {
        if (this.values) {
          let value = ''

          if (Array.isArray(this.values)) {
            if (this.field in PREFIX_MAP) {
              value = this.values.map(v => this.$t(`${PREFIX_MAP[this.field]}.${v}`)).join(', ')
            } else {
              value = this.values.join(', ')
            }
          } else {
            if (this.field in PREFIX_MAP) {
              if (this.values.includes(',')) {
                value = this.values.split(',').map(v => this.$t(`${PREFIX_MAP[this.field]}.${v}`)).join(', ')
              } else {
                value = this.$t(`${PREFIX_MAP[this.field]}.${this.values}`)
              }
            } else {
              value = this.values
            }
          }

          if (this.fieldType === 'date') {
            value = moment(value).format(this.$t('general.dateFormat'))
          }

          if (this.exclude) {
            if (this.groupOperator) {
              return this.$t('queryBuilder.fullSentence.excludeWithGroupOperator', { entity: this.$t(`campaignDesigner.targetDefinition.table.${this.table}`), attribute: this.$t(`campaignDesigner.targetDefinition.field.${this.field}`), operator: this.$t(`queryBuilder.${this.operator}`), value, groupOperator: this.$t(`operator.${this.groupOperator.toLowerCase()}`) })
            } else {
              return this.$t('queryBuilder.fullSentence.exclude', { entity: this.$t(`campaignDesigner.targetDefinition.table.${this.table}`), attribute: this.$t(`campaignDesigner.targetDefinition.field.${this.field}`), operator: this.$t(`queryBuilder.${this.operator}`), value })
            }
          } else {
            if (this.groupOperator) {
              return this.$t('queryBuilder.fullSentence.includeWithGroupOperator', { entity: this.$t(`campaignDesigner.targetDefinition.table.${this.table}`), attribute: this.$t(`campaignDesigner.targetDefinition.field.${this.field}`), operator: this.$t(`queryBuilder.${this.operator}`), value, groupOperator: this.$t(`operator.${this.groupOperator.toLowerCase()}`) })
            } else {
              return this.$t('queryBuilder.fullSentence.include', { entity: this.$t(`campaignDesigner.targetDefinition.table.${this.table}`), attribute: this.$t(`campaignDesigner.targetDefinition.field.${this.field}`), operator: this.$t(`queryBuilder.${this.operator}`), value })
            }
          }
        } else {
          if (this.exclude) {
            if (this.groupOperator) {
              return this.$t('queryBuilder.noValueSentence.excludeWithGroupOperator', { entity: this.$t(`campaignDesigner.targetDefinition.table.${this.table}`), attribute: this.$t(`campaignDesigner.targetDefinition.field.${this.field}`), operator: this.$t(`queryBuilder.${this.operator}`), groupOperator: this.$t(`operator.${this.groupOperator.toLowerCase()}`) })
            } else {
              return this.$t('queryBuilder.noValueSentence.exclude', { entity: this.$t(`campaignDesigner.targetDefinition.table.${this.table}`), attribute: this.$t(`campaignDesigner.targetDefinition.field.${this.field}`), operator: this.$t(`queryBuilder.${this.operator}`) })
            }
          } else {
            if (this.groupOperator) {
              return this.$t('queryBuilder.noValueSentence.includeWithGroupOperator', { entity: this.$t(`campaignDesigner.targetDefinition.table.${this.table}`), attribute: this.$t(`campaignDesigner.targetDefinition.field.${this.field}`), operator: this.$t(`queryBuilder.${this.operator}`), groupOperator: this.$t(`operator.${this.groupOperator.toLowerCase()}`) })
            } else {
              return this.$t('queryBuilder.noValueSentence.include', { entity: this.$t(`campaignDesigner.targetDefinition.table.${this.table}`), attribute: this.$t(`campaignDesigner.targetDefinition.field.${this.field}`), operator: this.$t(`queryBuilder.${this.operator}`) })
            }
          }
        }
      } else {
        return ''
      }
    },
    labelsPrefix () {
      if (PREFIX_MAP[this.field]) {
        return PREFIX_MAP[this.field]
      } else {
        return undefined
      }
    },
    operators () {
      if (this.table && this.field) {
        const operators = [
          'isnull',
          'isnotnull'
        ]

        if ('options' in this.config[this.table].fields[this.field]) {
          operators.push('eq')
          operators.push('ne')
          operators.push('in')
          operators.push('notin')
        }

        if (['date', 'integer'].includes(this.config[this.table].fields[this.field].type)) {
          operators.push('gte')
          operators.push('lte')
          operators.push('eq')
          operators.push('ne')
        }

        return operators
      } else {
        return []
      }
    },
    options () {
      if (this.table && this.field && this.operator) {
        if (this.field === 'model') {
          let brands
          if (this.brands) {
            brands = this.brands
          } else if (this.scope === 'campaign' || this.$route.path.includes('campaign')) {
            brands = this.$store.getters['campaignDesigner/currentCampaign'].brands
          } else {
            brands = []
          }
          return this.config.perm.fields.model.options.filter(m => brands.includes(m.brand))
        }
        return this.config[this.table].fields[this.field].options
      } else {
        return []
      }
    },
    tables () {
      if (this.config) {
        return Object.keys(this.config)
      } else {
        return []
      }
    }
  },
  watch: {
    id () {
      this.update(this)
    },
    field () {
      this.operator = ''
      this.values = ''
      this.update(this)
    },
    operator (newValue, oldValue) {
      if (oldValue !== newValue) {
        switch (newValue) {
          case 'gte':
          case 'lte':
          case 'eq':
          case 'ne':
            this.values = ''
            break
          case 'in':
          case 'notin':
            this.values = []
            break
          default:
            this.values = null
        }
        this.update(this)
      }
    },
    table () {
      this.update(this)
      this.field = ''
      this.operator = ''
      this.values = ''
    },
    values: {
      handler (val, old) {
        if (JSON.stringify(old) !== JSON.stringify(val)) {
          this.update(this)
        }
      },
      deep: true
    },
    'values.length': {
      handler (val, old) {
        if (JSON.stringify(old) !== JSON.stringify(val)) {
          this.update(this)
        }
      },
      deep: true
    },
    value: {
      handler (value) {
        const clonedValue = _.cloneDeep(value)
        this.id = clonedValue.id
        this.table = clonedValue.table

        this.$nextTick(() => {
          this.field = clonedValue.field

          this.$nextTick(() => {
            this.operator = clonedValue.operator

            this.$nextTick(() => {
              this.values = clonedValue.values
            })
          })
        })
      },
      deep: true
    }
  }
}
</script>
<style lang="scss" scoped>
  .button-container {
    display: flex;
    flex-direction: row;
  }

  .delete-button {
    color: #C23933;
    align-self: flex-end;
  }

  .item {
    align-self: flex-end;
  }
</style>
